import { SupportedCurrency } from '@va/constants';

export type WebsitesQueryFilter = {
  start: number;
  length: number;
  url: string;
  withSharedWebsites: boolean;
  withSubscription?: boolean;
};

export enum Platform {
  WIX = 'WIX',
  CUSTOM = 'CUSTOM',
  KYVIO = 'KYVIO',
  WEEBLY = 'WEEBLY',
  WORDPRESS = 'WORDPRESS',
  DRUPAL = 'DRUPAL',
  OCTOBER = 'OCTOBER',
  TYPO = 'TYPO',
  JOOMLA = 'JOOMLA',
  MAGENTO = 'MAGENTO',
  OPENCART = 'OPENC',
  AAAS = 'AAAS',
  PIXPA = 'PIXPA',
  // todo make sure these are used or remove them
  DRUPAL7 = 'DRUPAL7',
  DRUPAL8 = 'DRUPAL8',
  WIX_MINI = 'WIX_MINI',
}

export enum PageTypeEnum {
  dynamic = 'dynamic',
  static = 'static',
}

export enum UserRole {
  OWNER = 'owner',
  EDITOR = 'editor',
  WATCHER = 'watcher',
  DASHBOARD = 'dashboard',
}

export type StaticPageType = {
  key: string;
  url: string;
  title: string;
  type?: PageTypeEnum.static;
};

export type DynamicPageType = {
  key: string;
  name: string;
  pattern: string;
  type?: PageTypeEnum.dynamic;
};

export type SsrSettingsType = {
  key: string;
  paused: boolean;
  status: string;
  websiteKey: string;
  anyPage: boolean;
  clickAndScroll: boolean;
  minDuration: number;
  dynamicPages: DynamicPageType[];
  pages: StaticPageType[];
  pageKeys: string[];
  dynamicPageKeys: string[];
  urlPatterns: UrlPatternType[];
  error?: { message: string };
};

export enum UrlPatternTypeEnum {
  simple = 'simple',
  exact = 'exact',
  starts = 'startsWith',
  ends = 'endsWith',
  contains = 'contains',
}

export type UrlPatternType = {
  url: string;
  type: UrlPatternTypeEnum;
  id: string;
};

export enum URL_VIEW {
  title = 'title',
  url = 'url',
}

export enum WebsiteStatus {
  ACTIVE = 'ACTIVE',
  REJECTED = 'REJECTED',
  PENDING = 'PENDING',
}

/**
 * The website type when requested by ID (GET /websites/:websiteId)
 */
export type WebsiteResponse = {
  // Common properties
  anonymizeIps: boolean;
  businessSector: string;
  companyName: string;
  consentlessTracking: boolean;
  country: string | null;
  crawlerStatus: Record<
    'last' | 'lastSuccess',
    {
      status: number;
      error: string;
      errorCode: string;
      eventTs: number;
      authorId: string | null;
      seen: boolean;
      startTs: number;
      endTs: number;
      pageCount: number;
      nextScanTs: number;
    }
  >;
  currency: SupportedCurrency;
  email: string;
  eventAutoTracking: boolean;
  eventTracking: boolean;
  firstName: string;
  lastName: string;
  firstPremiumTs: number | null;
  firstWeekDay: number | null;
  frontendFlags: Record<string, unknown> | undefined | null;
  hadFirstVisit: boolean;
  id: string;
  ignoreVisitsKey: string;
  importTs: number | null;
  installDate: number;
  ipCountryISO: string;
  isPayable: boolean;
  lastResetDate: number | null;
  lastVisitTs: number | null;
  maxPrivacyMode: boolean;
  metaSiteId: string | null;
  numberOfMonthlyVisits: number;
  platform: Platform;
  resetDataRequested: null | boolean;
  status: WebsiteStatus;
  timezone: string;
  types: Array<string>;
  url: string;
  whitelistedDomainsOutgoing: boolean;

  // SA-specific properties
  regionalFormat: string | null;
  sumoActive: boolean;
  externalId: string | null;
  whiteLabel: boolean;
  hasCustomer: boolean;
  conversionCurrency: SupportedCurrency;
  ownerId: string;
  snippetCustomDomain: string | null;
  commissionStatus: boolean;
  commissionStatusUpdates: { timestamp: number; status: boolean }[];

  // Wix-specific properties
  appDate: string;
  businessSize: string;
  businessType: string;
  city: string | null;
  companyWebsite: string | null;
  discount: number;
  feedbackStatus: unknown;
  findUs: number | null;
  hasFirstLogin: boolean;
  lastInstallDate: number;
  latestDashboardAccesses: number[];
  locale: string;
  migrationStatus: unknown;
  numberOfDashboardAccesses: number;
  reviewStatus: boolean;
  state: string | null;
  street: string | null;
  vat: string | null;
  zipcode: string | null;
};

type UserType = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type ContributorType = {
  user: UserType;
  types: UserRole;
};

export type ContributorRequestType = {
  id: string;
  fullName: string | null;
  email: string;
  types: UserRole[];
};

/**
 * The website type when requesting a list (GET /websites)
 */
export type WebsiteType = {
  url: string;
  id: string;
  types: UserRole[];
  subscription: WebsiteSubscription | null;
  isWebsiteOwner: boolean;
  platform: Platform;
  status: WebsiteStatus;
  timezone: string;
  sumoActive: boolean;
  whiteLabel: boolean;
  installDate: number;
  contributors: ContributorType[];
  contributorRequests: ContributorRequestType[];
  commission: Record<string, number> | null;
  trackingCodeInstalled: boolean | null;
  owner: { email: string; firstName: string; lastName: string };
  unlimitedPlanActive?: boolean;
  commissionStatus?: boolean;
  [key: string]: unknown;
};

export type WebsitesDataType = {
  hasContributor: boolean;
  length: number;
  start: number;
  total: number;
  totalWhiteLabeled: number;
  websites: WebsiteType[];
};

export type WebsiteSubscription = {
  activeUntil: null | string;
  isActiveUntil?: null | string;
  id: string;
  planId: string;
  status: SubscriptionStatus;
  updateUrl: string;
  planName: string;
};

export enum SubscriptionStatus {
  // === Paddle ====
  TRIALING = 'Trialing',
  PAUSED = 'Paused',
  DELETED = 'Deleted',
  // === Common ====
  ACTIVE = 'Active',
  PAST_DUE = 'Past Due',
  // === Braintree ====
  PENDING = 'Pending',
  CANCELED = 'Canceled',
  EXPIRED = 'Expired',
}
